import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography, Button, IconButton, Divider, CircularProgress, FormHelperText} from '@mui/material';
import { ArrowBackIos, Visibility, VisibilityOff } from '@mui/icons-material';
import { completeSocialRegister } from '../../services/authService';
import Swal from 'sweetalert2';
import '../../styles/styles.css';

interface RegisterModalProps {
  open: boolean;
  onClose: () => void;
  onBack: () => void;
  initialData?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
  } | null;
}

const RegisterModal: React.FC<RegisterModalProps> = ({ open, onClose, onBack, initialData }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [profession, setProfession] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [birthDate, setBirthDate] = useState<string>('');
  const [email, setEmail] = useState('');
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false); 
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    idNumber: '',
    phoneNumber: '',
    profession: '',
    birthDate: '',
    email: '',
    profilePicture: '',
    username: '',
    password: ''

  });
  const [preview, setPreview] = useState<string | null>(null);


  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setProfession('');
    setIdNumber('');
    setPhoneNumber('');
    setBirthDate('');
    setProfilePicture(null);
    setUsername('');
    setPassword('');
    setPreview('');
    setEmail(initialData?.email || '');
    setErrors({
      firstName: '',
      lastName: '',
      idNumber: '',
      phoneNumber: '',
      profession: '',
      birthDate: '',
      email: '',
      profilePicture: '',
      username: '',
      password: ''
    });
  };

  useEffect(() => {
    const loadInitialData = async () => {
      if (initialData) {
        setFirstName(initialData.firstName || '');
        setLastName(initialData.lastName || '');
        setEmail(initialData.email || '');
  
      }
    };
  
    loadInitialData();
  }, [initialData]);
  

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setProfilePicture(file);

    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setPreview(previewUrl); 
    }
  };

  const handleRegister = async () => {
    setLoading(true);
    setErrors({
      firstName: '',
      lastName: '',
      profession: '',
      idNumber: '',
      phoneNumber: '',
      birthDate: '',
      email: '',
      profilePicture: '',
      username: '',
      password: ''
    });

    const newErrors = {
      firstName: firstName ? '' : 'El nombre es obligatorio.',
      lastName: lastName ? '' : 'El apellido es obligatorio.',
      profession: profession ? '' : 'La profesión es obligatoria.',
      idNumber: idNumber ? '' : 'La identificación es obligatoria.',
      phoneNumber: phoneNumber ? '' : 'El número de teléfono es obligatorio.',
      birthDate: birthDate ? '' : 'La fecha de nacimiento es obligatoria.',
      email: email ? '' : 'El correo electrónico es obligatorio.',
      profilePicture: profilePicture ? '' : 'La foto de perfil es obligatoria.',
      username: username ? '' : 'El nombre de usuario es obligatorio.',
      password: password
      ? password.length >= 8
        ? ''
        : 'La contraseña debe tener al menos 8 caracteres.'
      : 'La contraseña es obligatoria.',
    };

    if (Object.values(newErrors).some(error => error)) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      const requestBody = {
        fullName: `${firstName} ${lastName}`,
        position: profession,
        phoneNumber: phoneNumber,
        idNumber: idNumber,
        email: email,
        dateOfBirth: birthDate,
        photo: profilePicture!,
        username: username,
        password: password
      };

      const response = await completeSocialRegister(requestBody);


      if (response.status === 200) {
        Swal.fire({
          title: '¡Éxito!',
          text: 'El registro fue exitoso. Ahora puedes iniciar sesión con tu cuenta.',
          icon: 'success',
          confirmButtonText: 'Aceptar',
          customClass: {
            container: 'custom-popup', 
          },
        });
        handleClose();
      } else if(response.status === 409){
        Swal.fire({
          title: 'Error',
          text: response.message,
          icon: 'error',
          confirmButtonText: 'Aceptar',
          customClass: {
            container: 'custom-popup', 
          },
        })
      } else {
        Swal.fire({
          title: 'Error',
          text: 'Hubo un error al registrar. Por favor, inténtelo de nuevo.',
          icon: 'error',
          confirmButtonText: 'Aceptar',
          customClass: {
            container: 'custom-popup', 
          },
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: 'Hubo un problema con la conexión. Por favor, inténtelo de nuevo.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
        customClass: {
          container: 'custom-popup', 
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleBack = () => {
    resetForm();
    onBack();
  };


  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="register-modal-title"
      aria-describedby="register-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '60%', md: '35%' },
          maxHeight: '80vh',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#ccc',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#aaa',
          },
        }}
      >
        <IconButton
          sx={{
            position: 'absolute',
            top: 21,
            left: 11,
            color: 'text.primary',
            zIndex: 1,
          }}
          onClick={handleBack}
        >
          <ArrowBackIos />
        </IconButton>

        <Typography id="register-modal-title" variant="h6" component="h2" sx={{ textAlign: 'center' }}>
          Completar registro
        </Typography>

        <Divider sx={{ my: 2, width: '100%' }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <label htmlFor="firstName" style={{ display: 'block', marginBottom: '4px' }}>Nombre</label>
          <input
            id="firstName"
            type="text"
            placeholder="Ingresa tu nombre"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="inputField"

          />
          {errors.firstName && <FormHelperText error>{errors.firstName}</FormHelperText>}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <label htmlFor="lastName" style={{ display: 'block', marginBottom: '4px' }}>Apellidos</label>
          <input
            id="lastName"
            type="text"
            placeholder="Ingresa tus apellidos"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="inputField"
          />
          {errors.lastName && <FormHelperText error>{errors.lastName}</FormHelperText>}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <label htmlFor="profession" style={{ display: 'block', marginBottom: '4px' }}>Profesión</label>
          <input
            id="profession"
            type="text"
            placeholder="Ingresa tu profesión"
            value={profession}
            onChange={(e) => setProfession(e.target.value)}
            className="inputField"
          />
          {errors.profession && <FormHelperText error>{errors.profession}</FormHelperText>}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <label htmlFor="idNumber" style={{ display: 'block', marginBottom: '4px' }}>Identificación</label>
          <input
            id="idNumber"
            type="text"
            placeholder="Ingresa tu identificación"
            value={idNumber}
            onChange={(e) => setIdNumber(e.target.value)}
            className="inputField"
          />
          {errors.idNumber && <FormHelperText error>{errors.idNumber}</FormHelperText>}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <label htmlFor="phoneNumber" style={{ display: 'block', marginBottom: '4px' }}>Número de teléfono</label>
          <input
            id="phoneNumber"
            type="text"
            placeholder="Ingresa tu número de teléfono"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            className="inputField"
          />
          {errors.phoneNumber && <FormHelperText error>{errors.phoneNumber}</FormHelperText>}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <label htmlFor="birthDate" style={{ display: 'block', marginBottom: '4px' }}>Fecha de Nacimiento</label>
          <input
            id="birthDate"
            type="date"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
            className="inputField"
          />
          {errors.birthDate && <FormHelperText error>{errors.birthDate}</FormHelperText>}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <label htmlFor="email" style={{ display: 'block', marginBottom: '4px' }}>Correo Electrónico</label>
          <input
            id="email"
            type="email"
            readOnly
            placeholder="Correo Electrónico"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="inputField"
          />
          {errors.email && <FormHelperText error>{errors.email}</FormHelperText>}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <label htmlFor="profilePicture" style={{ marginBottom: '4px' }}>Foto de Perfil</label>
            <input
              id="profilePicture"
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e)}
            />
            {preview && (
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  mt: 1,
                }}
              >
                <img
                  src={preview}
                  alt="Vista previa"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            )}
            {errors.profilePicture && <FormHelperText error>{errors.profilePicture}</FormHelperText>}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <label htmlFor="username" style={{ display: 'block', marginBottom: '4px' }}>Nombre de Usuario</label>
          <input
            id="username"
            type="text"
            placeholder="Ingresa tu nombre de usuario"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="inputField"
          />
          {errors.username && <FormHelperText error>{errors.username}</FormHelperText>}
        </Box>

        {/* Contraseña */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <label htmlFor="password" style={{ display: 'block', marginBottom: '4px' }}>Contraseña</label>
          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Ingresa tu contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="inputField"
              style={{
                width: '100%',
                height: '100%',
                paddingBlock: 14,
                paddingRight: '40px',
                boxSizing: 'border-box',

              }}
            />
            <IconButton
              onClick={() => setShowPassword(!showPassword)}
              edge="end"
              sx={{
                position: 'absolute',
                right: 20, 
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </Box>
          {errors.password && <FormHelperText error>{errors.password}</FormHelperText>}
        </Box>

        <Button
          variant="contained"
          onClick={handleRegister}
          sx={{
            mt: 2,
            backgroundColor: 'green',
            color: 'white',
            '&:hover': {
              backgroundColor: 'darkgreen',
            },
            position: 'relative',
            minHeight: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          fullWidth
          disabled={loading}
        >
          {loading ? (
            <CircularProgress
              size={24}
              color="inherit"
            />
          ): (
            'Aceptar y Continuar'
          )}
        </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default RegisterModal;
